import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

/*
  About Us — Aamira Jewellery
  ─────────────────────────────
  Editorial luxury layout:
  1. Full-bleed hero with parallax model image + large title
  2. Story section — text left, image right
  3. Values strip — 4 pillars
  4. Split editorial — image mosaic + manifesto quote
  5. Stats bar
  6. Team / face of brand
  7. CTA banner
*/

const AboutPage = () => {

  /* Parallax on hero image */
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(heroScroll, [0, 1], ['0%', '28%']);

  /* Section refs */
  const storyRef   = useRef(null);
  const valuesRef  = useRef(null);
  const mosaicRef  = useRef(null);
  const statsRef   = useRef(null);
  const teamRef    = useRef(null);
  const ctaRef     = useRef(null);

  const storyInView  = useInView(storyRef,  { once: true, margin: '-80px' });
  const valuesInView = useInView(valuesRef, { once: true, margin: '-60px' });
  const mosaicInView = useInView(mosaicRef, { once: true, margin: '-60px' });
  const statsInView  = useInView(statsRef,  { once: true, margin: '-60px' });
  const teamInView   = useInView(teamRef,   { once: true, margin: '-60px' });
  const ctaInView    = useInView(ctaRef,    { once: true, margin: '-60px' });

  const VALUES = [
    { icon: '✦', title: 'Craftsmanship', desc: 'Every piece is meticulously crafted by skilled artisans who treat each jewel as a work of art.' },
    { icon: '◈', title: 'Heritage',      desc: 'Rooted in India\'s rich jewellery tradition, we carry centuries of design wisdom into modern form.' },
    { icon: '◇', title: 'Authenticity',  desc: 'We source only genuine materials and maintain complete transparency in every creation.' },
    { icon: '○', title: 'Elegance',      desc: 'Beauty that endures — timeless designs made to be worn, treasured, and passed on.' },
  ];

  const STATS = [
    { value: '5,000+', label: 'Happy Customers' },
    { value: '21',     label: 'Collections' },
    { value: '500+',   label: 'Unique Designs' },
    { value: '3+',     label: 'Years of Excellence' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');

        /* ══ PAGE ══ */
        .ab-page {
          background: #FAF8F4;
          font-family: 'Jost', sans-serif;
          padding-top: 162px;  /* navbar height */
          overflow-x: hidden;
        }

        /* grain overlay */
        .ab-page::before {
          content: '';
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E");
        }

        /* ══ 1. HERO ══ */
        .ab-hero {
          position: relative;
          height: 86vh;
          min-height: 560px;
          overflow: hidden;
          z-index: 1;
        }
        .ab-hero-img {
          position: absolute;
          inset: -10% 0;
          will-change: transform;
        }
        .ab-hero-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
        }
        /* deep rich overlay */
        .ab-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(12,9,5,0.82) 0%,
            rgba(12,9,5,0.42) 55%,
            rgba(12,9,5,0.65) 100%
          );
          z-index: 2;
        }
        .ab-hero-content {
          position: absolute;
          inset: 0;
          z-index: 3;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: clamp(32px, 6vw, 90px);
          padding-bottom: clamp(48px, 8vh, 90px);
        }
        .ab-hero-eyebrow {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.35em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
        }
        .ab-hero-eyebrow::before {
          content: '';
          width: 32px; height: 1px;
          background: #B08D57;
        }
        .ab-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(44px, 7vw, 96px);
          font-weight: 700;
          color: #FAF8F4;
          line-height: 0.95;
          letter-spacing: -0.02em;
          margin-bottom: 24px;
        }
        .ab-hero-title em {
          font-style: italic;
          font-weight: 400;
          color: #C9A96E;
          display: block;
        }
        .ab-hero-sub {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(17px, 2vw, 22px);
          font-style: italic;
          font-weight: 300;
          color: rgba(250,248,244,0.65);
          max-width: 480px;
          line-height: 1.7;
          letter-spacing: 0.02em;
        }
        /* scroll hint */
        .ab-scroll-hint {
          position: absolute;
          bottom: 32px;
          right: clamp(24px, 5vw, 80px);
          z-index: 3;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          opacity: 0.5;
        }
        .ab-scroll-hint span {
          font-size: 8px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: #FAF8F4;
          writing-mode: vertical-rl;
        }
        .ab-scroll-line {
          width: 1px;
          height: 48px;
          background: linear-gradient(to bottom, #B08D57, transparent);
          animation: ab-scroll-line 1.6s ease-in-out infinite;
        }
        @keyframes ab-scroll-line {
          0%   { transform: scaleY(0); transform-origin: top; }
          50%  { transform: scaleY(1); transform-origin: top; }
          51%  { transform: scaleY(1); transform-origin: bottom; }
          100% { transform: scaleY(0); transform-origin: bottom; }
        }

        /* ══ SHARED LAYOUT ══ */
        .ab-section {
          position: relative;
          z-index: 1;
          padding: 100px clamp(24px, 6vw, 100px);
          max-width: 1280px;
          margin: 0 auto;
        }
        .ab-section--full {
          max-width: 100%;
          padding-left: 0;
          padding-right: 0;
        }

        .ab-eyebrow {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .ab-eyebrow::before {
          content: '';
          width: 28px; height: 1px;
          background: #B08D57;
          flex-shrink: 0;
        }

        /* ══ 2. STORY ══ */
        .ab-story-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(40px, 6vw, 90px);
          align-items: center;
        }
        .ab-story-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(30px, 4vw, 50px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.01em;
          margin-bottom: 24px;
        }
        .ab-story-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .ab-story-body {
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          font-weight: 300;
          color: #5C5247;
          line-height: 1.95;
          letter-spacing: 0.02em;
          margin-bottom: 18px;
        }
        .ab-story-body + .ab-story-body { margin-top: 0; }

        .ab-gold-divider {
          width: 48px; height: 1px;
          background: linear-gradient(to right, #B08D57, transparent);
          margin: 28px 0;
        }

        /* story image */
        .ab-story-img-wrap {
          position: relative;
        }
        .ab-story-img-frame {
          position: absolute;
          top: 24px; left: -20px;
          width: 100%; height: 100%;
          border: 1px solid rgba(176,141,87,0.3);
          border-radius: 2px;
          pointer-events: none;
        }
        .ab-story-img {
          position: relative;
          width: 100%;
          aspect-ratio: 4/5;
          overflow: hidden;
          border-radius: 2px;
          box-shadow: 0 28px 64px rgba(28,26,24,0.14);
        }
        .ab-story-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
          transition: transform 0.8s ease;
        }
        .ab-story-img:hover img { transform: scale(1.04); }
        /* gold wash on bottom */
        .ab-story-img::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(176,141,87,0.18) 0%, transparent 50%);
          pointer-events: none;
        }

        /* floating stat card */
        .ab-story-stat {
          position: absolute;
          bottom: -20px;
          right: -24px;
          background: #1C1A18;
          padding: 18px 24px;
          z-index: 4;
          min-width: 130px;
          text-align: center;
        }
        .ab-story-stat-num {
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          font-weight: 700;
          color: #B08D57;
          display: block;
          letter-spacing: -0.02em;
        }
        .ab-story-stat-label {
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(250,248,244,0.6);
          margin-top: 4px;
        }

        /* ══ 3. VALUES ══ */
        .ab-values-bg {
          background: #1C1A18;
          padding: 80px clamp(24px, 6vw, 100px);
          position: relative;
          z-index: 1;
          overflow: hidden;
        }
        /* subtle gold pattern */
        .ab-values-bg::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 20% 50%, rgba(176,141,87,0.08) 0%, transparent 60%),
            radial-gradient(ellipse at 80% 50%, rgba(176,141,87,0.06) 0%, transparent 60%);
          pointer-events: none;
        }
        .ab-values-inner {
          max-width: 1280px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        .ab-values-header {
          text-align: center;
          margin-bottom: 56px;
        }
        .ab-values-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(26px, 3.5vw, 42px);
          font-weight: 700;
          color: #FAF8F4;
          letter-spacing: -0.01em;
        }
        .ab-values-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .ab-values-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2px;
        }
        .ab-value-card {
          background: rgba(250,248,244,0.03);
          border: 1px solid rgba(176,141,87,0.12);
          padding: 36px 28px;
          transition: background 0.3s, border-color 0.3s;
          cursor: default;
        }
        .ab-value-card:hover {
          background: rgba(176,141,87,0.07);
          border-color: rgba(176,141,87,0.3);
        }
        .ab-value-icon {
          font-size: 22px;
          color: #B08D57;
          display: block;
          margin-bottom: 18px;
          transition: transform 0.3s;
        }
        .ab-value-card:hover .ab-value-icon { transform: scale(1.15) rotate(15deg); }
        .ab-value-title {
          font-family: 'Playfair Display', serif;
          font-size: 18px;
          font-weight: 600;
          color: #FAF8F4;
          margin-bottom: 10px;
          letter-spacing: -0.01em;
        }
        .ab-value-desc {
          font-size: 13px;
          font-weight: 300;
          color: rgba(250,248,244,0.5);
          line-height: 1.85;
          letter-spacing: 0.02em;
        }

        /* ══ 4. MOSAIC + QUOTE ══ */
        .ab-mosaic-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          min-height: 600px;
        }
        /* left: image mosaic */
        .ab-mosaic-left {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 4px;
        }
        .ab-mosaic-img {
          overflow: hidden;
          position: relative;
        }
        .ab-mosaic-img img {
          width: 100%; height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.7s ease;
        }
        .ab-mosaic-img:hover img { transform: scale(1.06); }
        /* first image spans 2 rows */
        .ab-mosaic-img:first-child {
          grid-row: 1 / 3;
        }

        /* right: quote panel */
        .ab-mosaic-right {
          background: #F0E8D8;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: clamp(40px, 6vw, 80px);
          position: relative;
          overflow: hidden;
        }
        .ab-mosaic-right::before {
          content: '"';
          position: absolute;
          top: -20px; left: 20px;
          font-family: 'Cormorant Garamond', serif;
          font-size: 220px;
          font-weight: 300;
          color: rgba(176,141,87,0.1);
          line-height: 1;
          pointer-events: none;
          user-select: none;
        }
        .ab-manifesto-label {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ab-manifesto-label::before {
          content: '';
          width: 24px; height: 1px;
          background: #B08D57;
        }
        .ab-manifesto-quote {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(22px, 3vw, 32px);
          font-weight: 300;
          font-style: italic;
          color: #1C1A18;
          line-height: 1.55;
          letter-spacing: 0.01em;
          margin-bottom: 28px;
        }
        .ab-manifesto-quote strong {
          font-weight: 400;
          color: #B08D57;
          font-style: normal;
        }
        .ab-manifesto-attr {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #8A7E72;
        }
        .ab-manifesto-body {
          font-size: 13.5px;
          font-weight: 300;
          color: #5C5247;
          line-height: 1.9;
          letter-spacing: 0.02em;
          margin-top: 24px;
          max-width: 380px;
        }

        /* ══ 5. STATS ══ */
        .ab-stats-bar {
          background: #B08D57;
          padding: 56px clamp(24px, 6vw, 100px);
          position: relative;
          z-index: 1;
        }
        .ab-stats-inner {
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2px;
        }
        .ab-stat-item {
          text-align: center;
          padding: 20px;
          border-right: 1px solid rgba(255,255,255,0.2);
        }
        .ab-stat-item:last-child { border-right: none; }
        .ab-stat-num {
          font-family: 'Playfair Display', serif;
          font-size: clamp(32px, 4vw, 52px);
          font-weight: 700;
          color: #FAF8F4;
          letter-spacing: -0.02em;
          display: block;
          line-height: 1;
          margin-bottom: 8px;
        }
        .ab-stat-label {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.65);
        }

        /* ══ 6. TEAM / FACE ══ */
        .ab-team-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 20px;
        }
        .ab-team-card {
          position: relative;
          border-radius: 2px;
          overflow: hidden;
          cursor: pointer;
        }
        .ab-team-img {
          width: 100%;
          aspect-ratio: 3/4;
          overflow: hidden;
          position: relative;
        }
        .ab-team-img img {
          width: 100%; height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
          transition: transform 0.7s ease;
        }
        .ab-team-card:hover .ab-team-img img { transform: scale(1.06); }
        .ab-team-img::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(28,26,24,0.75) 0%, transparent 55%);
        }
        .ab-team-info {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          padding: 20px;
          z-index: 2;
          transform: translateY(4px);
          transition: transform 0.3s;
        }
        .ab-team-card:hover .ab-team-info { transform: translateY(0); }
        .ab-team-role {
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 4px;
        }
        .ab-team-name {
          font-family: 'Playfair Display', serif;
          font-size: 18px;
          font-weight: 600;
          color: #FAF8F4;
          letter-spacing: -0.01em;
        }

        /* ══ 7. CTA BANNER ══ */
        .ab-cta {
          position: relative;
          z-index: 1;
          overflow: hidden;
          min-height: 420px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ab-cta-bg img {
          position: absolute;
          inset: 0;
          width: 100%; height: 100%;
          object-fit: cover;
          object-position: center 30%;
          display: block;
        }
        .ab-cta-overlay {
          position: absolute;
          inset: 0;
          background: rgba(12,9,5,0.72);
        }
        .ab-cta-content {
          position: relative;
          z-index: 2;
          text-align: center;
          padding: clamp(48px, 8vw, 90px);
        }
        .ab-cta-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 4.5vw, 54px);
          font-weight: 700;
          color: #FAF8F4;
          line-height: 1.1;
          letter-spacing: -0.01em;
          margin-bottom: 12px;
        }
        .ab-cta-title em {
          font-style: italic;
          font-weight: 400;
          color: #C9A96E;
        }
        .ab-cta-sub {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(15px, 1.8vw, 20px);
          font-style: italic;
          font-weight: 300;
          color: rgba(250,248,244,0.6);
          margin-bottom: 36px;
          letter-spacing: 0.02em;
        }
        .ab-cta-btn {
          display: inline-block;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #1C1A18;
          background: #B08D57;
          text-decoration: none;
          padding: 16px 40px;
          position: relative;
          overflow: hidden;
          transition: color 0.3s;
        }
        .ab-cta-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: #FAF8F4;
          transform: translateX(-101%);
          transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
          z-index: 0;
        }
        .ab-cta-btn:hover::after { transform: translateX(0); }
        .ab-cta-btn span { position: relative; z-index: 1; }

        /* ══ RESPONSIVE ══ */
        @media (max-width: 900px) {
          .ab-story-grid { grid-template-columns: 1fr; }
          .ab-story-img-wrap { max-width: 420px; margin: 0 auto; }
          .ab-values-grid { grid-template-columns: repeat(2, 1fr); }
          .ab-mosaic-grid { grid-template-columns: 1fr; }
          .ab-mosaic-left { height: 400px; }
          .ab-stats-inner { grid-template-columns: repeat(2, 1fr); }
          .ab-stat-item:nth-child(2) { border-right: none; }
          .ab-team-grid { grid-template-columns: 1fr 1fr; }
          .ab-page { padding-top: 148px; }
        }
        @media (max-width: 560px) {
          .ab-values-grid { grid-template-columns: 1fr; }
          .ab-team-grid { grid-template-columns: 1fr; }
          .ab-stats-inner { grid-template-columns: repeat(2, 1fr); }
          .ab-hero { height: 75vh; }
          .ab-page { padding-top: 105px; }
        }
      `}</style>

      <div className="ab-page">

        {/* ══════════════════════════════
            1. HERO
        ══════════════════════════════ */}
        <div className="ab-hero" ref={heroRef}>
          <motion.div className="ab-hero-img" style={{ y: heroY }}>
            <img
              src="/about1.jpg"
              alt="Aamira jewellery model"
            />
          </motion.div>
          <div className="ab-hero-overlay" />
          <div className="ab-hero-content">
            <motion.p
              className="ab-hero-eyebrow"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              Aamira Jewellery
            </motion.p>
            <motion.h1
              className="ab-hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
            >
              The Art of
              <em>Adornment</em>
            </motion.h1>
            <motion.p
              className="ab-hero-sub"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
            >
              Born from a love of beauty and a deep respect for craft —
              Aamira is where heritage meets the modern woman.
            </motion.p>
          </div>
          <div className="ab-scroll-hint">
            <div className="ab-scroll-line" />
            <span>Scroll</span>
          </div>
        </div>
        {/* ══════════════════════════════
            2. OUR STORY
        ══════════════════════════════ */}
        <div className="ab-section" ref={storyRef}>
          <div className="ab-story-grid">

            {/* Left: text */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={storyInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <p className="ab-eyebrow">Our Story</p>
              <h2 className="ab-story-title">
                Jewellery that carries <em>your story</em>
              </h2>
              <p className="ab-story-body">
                Aamira was founded with a single belief — that every woman deserves jewellery
                that feels like it was made for her alone. Nestled in the heart of Kannur, Kerala,
                we bring together the finest artisan traditions of South India with designs that
                speak to the contemporary woman.
              </p>
              <div className="ab-gold-divider" />
              <p className="ab-story-body">
                From our first collection to the Aamira Luxe signature line, every piece
                carries the mark of intentional design and uncompromising quality. We believe
                jewellery is not just an accessory — it is a language, a memory, an heirloom
                waiting to be passed on.
              </p>
              <p className="ab-story-body" style={{ marginTop: '18px' }}>
                Our store at Capitol Mall, Kannur has become a destination for brides,
                gift-givers, and jewellery lovers who want something more than ordinary —
                something that truly endures.
              </p>
            </motion.div>

            {/* Right: image */}
            <motion.div
              className="ab-story-img-wrap"
              initial={{ opacity: 0, x: 50 }}
              animate={storyInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div className="ab-story-img-frame" />
              <div className="ab-story-img">
                <img
                  src="/11.jpg"
                  alt="Jewellery model"
                />
              </div>
              <div className="ab-story-stat">
                <span className="ab-story-stat-num">500+</span>
                <p className="ab-story-stat-label">Unique Designs</p>
              </div>
            </motion.div>

          </div>
        </div>

        {/* ══════════════════════════════
            3. VALUES
        ══════════════════════════════ */}
        <div className="ab-values-bg" ref={valuesRef}>
          <div className="ab-values-inner">
            <motion.div
              className="ab-values-header"
              initial={{ opacity: 0, y: 20 }}
              animate={valuesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '10px', fontWeight: 500, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#B08D57', marginBottom: '12px' }}>
                What We Stand For
              </p>
              <h2 className="ab-values-title">
                Our <em>Four Pillars</em>
              </h2>
            </motion.div>

            <div className="ab-values-grid">
              {VALUES.map((v, i) => (
                <motion.div
                  key={v.title}
                  className="ab-value-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                >
                  <span className="ab-value-icon">{v.icon}</span>
                  <h3 className="ab-value-title">{v.title}</h3>
                  <p className="ab-value-desc">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════
            4. MOSAIC + MANIFESTO
        ══════════════════════════════ */}
        <div className="ab-mosaic-grid" ref={mosaicRef}>

          {/* Image mosaic */}
          <motion.div
            className="ab-mosaic-left"
            initial={{ opacity: 0, x: -40 }}
            animate={mosaicInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="ab-mosaic-img">
              <img
                src="/12.jpg"
                alt="Model wearing jewellery"
              />
            </div>
            <div className="ab-mosaic-img">
              <img
                src="/13.jpg"
                alt="Gold necklace"
              />
            </div>
            <div className="ab-mosaic-img">
              <img
                src="/14.jpg"
                alt="Diamond ring"
              />
            </div>
          </motion.div>

          {/* Manifesto */}
          <motion.div
            className="ab-mosaic-right"
            initial={{ opacity: 0, x: 40 }}
            animate={mosaicInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <p className="ab-manifesto-label">Our Manifesto</p>
            <p className="ab-manifesto-quote">
              We don't just sell jewellery —
              we craft <strong>moments of beauty</strong>
              that stay with you long after
              the occasion has passed.
            </p>
            <p className="ab-manifesto-attr">— Aamira, Kannur</p>
            <p className="ab-manifesto-body">
              Every ring, necklace, bangle and earring that leaves our store carries
              with it the warmth of our craft, the integrity of our materials, and
              the promise that beauty should be accessible, enduring, and truly yours.
            </p>
          </motion.div>
        </div>

        {/* ══════════════════════════════
            5. STATS
        ══════════════════════════════ */}
        <div className="ab-stats-bar" ref={statsRef}>
          <div className="ab-stats-inner">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                className="ab-stat-item"
                initial={{ opacity: 0, y: 20 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: i * 0.1 }}
              >
                <span className="ab-stat-num">{s.value}</span>
                <p className="ab-stat-label">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════
            6. FACES / EDITORIAL MODELS
        ══════════════════════════════ */}
        <div className="ab-section" ref={teamRef}>
          <motion.div
            style={{ marginBottom: '40px' }}
            initial={{ opacity: 0, y: 20 }}
            animate={teamInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="ab-eyebrow">The Aamira Woman</p>
            <h2 className="ab-story-title" style={{ maxWidth: '480px' }}>
              She wears her <em>confidence</em> like jewellery
            </h2>
          </motion.div>

          <div className="ab-team-grid">
            {[
              {
                src: '/16.jpg',
                name: 'The Bride',
                role: 'Bridal Collection',
              },
              {
                src: '/17.jpg',
                name: 'The Modern Woman',
                role: 'Everyday Luxury',
              },
              {
                src: '19.jpg',
                name: 'The Traditionalist',
                role: 'Heritage Series',
              },
            ].map((person, i) => (
              <motion.div
                key={person.name}
                className="ab-team-card"
                initial={{ opacity: 0, y: 40 }}
                animate={teamInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: i * 0.12 }}
              >
                <div className="ab-team-img">
                  <img src={person.src} alt={person.name} loading="lazy" />
                </div>
                <div className="ab-team-info">
                  <p className="ab-team-role">{person.role}</p>
                  <h3 className="ab-team-name">{person.name}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════
            7. CTA BANNER
        ══════════════════════════════ */}
        <div className="ab-cta" ref={ctaRef}>
          <div className="ab-cta-bg">
            <img
              src="https://images.pexels.com/photos/1191531/pexels-photo-1191531.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="jewellery background"
            />
          </div>
          <div className="ab-cta-overlay" />
          <motion.div
            className="ab-cta-content"
            initial={{ opacity: 0, y: 30 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.75 }}
          >
            <h2 className="ab-cta-title">
              Find your perfect <em>piece</em>
            </h2>
            <p className="ab-cta-sub">
              Explore 21 collections — from everyday elegance to bridal splendour.
            </p>
            <Link to="/collections" className="ab-cta-btn">
              <span>Shop Collections</span>
            </Link>
          </motion.div>
        </div>

      </div>
    </>
  );
};

export default AboutPage;